import { Box, Theme, Typography } from '@mui/material';
import React, { FC, ReactElement } from 'react';

interface Props {
  /** The section label itself, written already in the upper case it is drawn in. */
  label: string;
  /** The count beside it, spelled the way its own screen spells it. */
  count: string;
  /**
   * Test id for the count badge.
   * <p>
   * The label and the count are separate elements with a gap between them rather than one string,
   * so there is no single node reading «ФОТОГРАФИИ 3 из 10» for a test to match on.
   */
  testId?: string;
}

/**
 * A section label followed by its count in a rounded badge, as every photo section is headed.
 * <p>
 * The count is kept out of the label rather than joined into one string: the label reads as a
 * heading and the badge as a running total, and the two are styled apart.
 */
export const SectionCountLabel: FC<Props> = (props: Props): ReactElement => {
  const { label, count, testId } = props;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant={'label1'} component={'p'} sx={{ color: (theme: Theme): string => theme.colors.grayText }}>
        {label}
      </Typography>
      <Box
        sx={(theme: Theme) => ({
          display: 'inline-flex',
          alignItems: 'center',
          padding: theme.spacing(0.25, 1),
          borderRadius: theme.spacing(1),
          backgroundColor: theme.colors.lightBlueBackground,
        })}>
        <Typography
          variant={'body3'}
          data-testid={testId}
          sx={{ color: (theme: Theme): string => theme.colors.grayText }}>
          {count}
        </Typography>
      </Box>
    </Box>
  );
};
